<template>
  <f7-page name="home" @page:init="pageInit">
    <f7-navbar large>
      <f7-nav-title sliding>Pelikánec</f7-nav-title>
      <f7-nav-title-large>Ahoj, {{ userName }}</f7-nav-title-large>
      <f7-nav-right v-if="user?.companyName" class="custom-ios">
        <p class="company margin-right">{{ user.companyName }}</p>
      </f7-nav-right>
    </f7-navbar>

    <f7-link
      v-if="cardLoanTotal"
      href="/parking/reservations/"
      no-link-class
      class="display-block"
      aria-label="Zobraziť karty na vrátenie v mojich rezerváciách"
    >
      <f7-card class="cards-warning-bg parking-notice">
        <f7-card-header class="bold display-block">
          <span class="title display-block">Máte parkovacie karty na vrátenie</span>
          <span class="display-block subtitle">Vrátenie potvrďte v časti Moje rezervácie.</span>
        </f7-card-header>
      </f7-card>
    </f7-link>

    <f7-card v-if="banInfo" class="danger-bg ban-bg parking-notice">
      <f7-card-header class="bold display-block">
        <span class="title display-block">
          <f7-icon ios="f7:lock_fill" md="material:block" class="ban-icon" />
          Zákaz rezervácie
        </span>
        <span class="display-block subtitle">
          Máte obmedzené rezervácie do <b>{{ banUntilFormatted }}</b
          >. Termíny po tomto dátume si môžete rezervovať bez obmedzení.
        </span>
      </f7-card-header>
    </f7-card>

    <f7-card>
      <f7-card-header class="margin-bottom-half">
        <span class="title">Parkovanie v Eurovei</span>
        <f7-icon ios="f7:car_fill" md="material:directions_car" />
      </f7-card-header>
      <f7-card-content :class="{ 'text-align-center': !isParkingServiceLoaded }">
        <f7-preloader v-if="!isParkingServiceLoaded" color="primary" />
        <section v-else>
          <f7-button popup-open=".parking-reservation-form" large round tonal color="secondary"
            >Rezervovať si parkovanie</f7-button
          >
          <f7-list class="margin-top-half" strong inset dividers-ios>
            <f7-list-item link="/parking/reservations/">Moje rezervácie</f7-list-item>
            <f7-list-item link="/parking/vehicles/">Správa vozidiel</f7-list-item>
          </f7-list>
        </section>
      </f7-card-content>
    </f7-card>

    <f7-block-title>Ďalšie informácie</f7-block-title>
    <f7-list strong inset dividersIos>
      <f7-list-item link="/about/" title="O aplikácii"></f7-list-item>
    </f7-list>

    <template #fixed>
      <Menu />
    </template>
  </f7-page>
</template>

<script setup lang="ts">
import { useStore } from 'framework7-vue'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'

import Reservations from '@/assets/classes/parking/Reservations'
import Vehicles from '@/assets/classes/parking/Vehicles'
import { refreshBanStatus } from '@/assets/composables/ban'
import type { BanInfo } from '@/assets/composables/types'
import { globalAppError } from '@/assets/composables/utils'
import Menu from '@/components/menu.vue'

const user = useStore('user')
const cardLoanTotal = useStore('cardLoanTotal')
const banInfo = useStore('banInfo')

const userName = computed<string>(() => user?.value?.name?.split(' ')[0])
const isParkingServiceLoaded = ref<boolean>(false)

const banUntilFormatted = computed<string>(() => {
  const info = banInfo.value as BanInfo | null
  return info?.bannedUntil?.isValid ? info.bannedUntil.toLocaleString(DateTime.DATE_FULL) : ''
})

const pageInit = () => {
  // Ban state can change server-side (auto-ban or a backoffice unban) — refresh
  // it whenever the home page loads. Non-fatal, so it stays out of the Promise.all.
  refreshBanStatus()
  Promise.all([Reservations.fetchNewData(), Vehicles.fetchNewData()])
    .catch((error) => {
      globalAppError(error)
    })
    .finally(() => {
      isParkingServiceLoaded.value = true
    })
}
</script>

<style scoped>
.parking-notice {
  --f7-card-header-font-size: 17px;
}

@media (prefers-reduced-motion: no-preference) {
  .parking-notice {
    animation: parking-notice-appear 160ms ease-out;
  }

  @keyframes parking-notice-appear {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}

.ios .navbar .custom-ios {
  backdrop-filter: none;
  box-shadow: none;
  background-color: transparent;
}

.company {
  font-size: 0.8em;
  letter-spacing: 0.6px;
  color: #333;
}

.subtitle {
  font-size: 0.8em;
}

.cards-warning-bg {
  background-color: #fff3cd;
  color: #7a5b00;
}

.ban-bg {
  color: #842029;
}

.ban-bg .ban-icon {
  font-size: 1.1em;
  vertical-align: text-bottom;
  margin-right: 4px;
}
</style>
